// CeKKeU 2.0 — Stress Simulator 2.0 (what-if), based on DAILY TRACKING data.
// PURE, CLIENT-SIDE ONLY. Independent from the FROZEN V2.1 resilience stress test.

const DAY_MS = 86400000;
const d = (x) => (x instanceof Date ? x : new Date(x));

// Derive the family's current monthly baseline from tracking data.
export function simulatorBase(finance, now = new Date()) {
  const income = (finance?.incomeSources || []).filter((s) => s.active !== false);
  const recurring = (finance?.recurringExpenses || []).filter((s) => s.active !== false);
  const debts = (finance?.debts || []).filter((s) => s.active !== false);
  const emergency = Number(finance?.emergencyFund?.current_amount) || 0;
  const tx = (finance?.transactions || []).filter((t) => t && t.date);

  const monthlyIncome = income.reduce((s, x) => s + (Number(x.amount) || 0), 0);
  const monthlyRecurring = recurring.reduce((s, x) => s + (Number(x.amount) || 0), 0);
  const monthlyDebt = debts.reduce((s, x) => s + (Number(x.monthly_payment) || 0), 0);

  // typical monthly variable spending from the last ~30 days of actual entries
  const expTx = tx.filter((t) => t.type === "expense");
  let monthlyVariable = 0;
  if (expTx.length) {
    const first = Math.min(...expTx.map((t) => +d(t.date)));
    const historyDays = Math.max(1, Math.round((+d(now) - first) / DAY_MS) + 1);
    const since = +d(now) - 30 * DAY_MS;
    const recentSum = expTx.filter((t) => +d(t.date) >= since).reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const effDays = Math.min(30, historyDays);
    monthlyVariable = effDays > 0 ? (recentSum / effDays) * 30 : 0;
  }

  return {
    income,
    monthlyIncome,
    monthlyRecurring,
    monthlyDebt,
    monthlyVariable,
    committed: monthlyRecurring + monthlyDebt,
    emergency,
    hasIncome: monthlyIncome > 0,
  };
}

function classify(monthlyBalance, runwayMonths, coverageMonths) {
  if (monthlyBalance >= 0) {
    // positive cashflow → healthy unless the buffer is thin
    if (coverageMonths >= 3) return { level: "sehat", label: "Sehat" };
    if (coverageMonths >= 1) return { level: "waspada", label: "Waspada" };
    return { level: "waspada", label: "Waspada" };
  }
  // deficit → never fully healthy
  if (runwayMonths < 3) return { level: "perhatian", label: "Perlu Perhatian" };
  return { level: "waspada", label: "Waspada" };
}

// params: { incomeDropPct 0..1, expenseUpPct 0..1, oneTime (Rp), newDebtMonthly (Rp), lostSourceId }
export function simulateStress(finance, params = {}, now = new Date()) {
  const base = simulatorBase(finance, now);
  const {
    incomeDropPct = 0,
    expenseUpPct = 0,
    oneTime = 0,
    newDebtMonthly = 0,
    lostSourceId = null,
  } = params;

  const lost = lostSourceId ? base.income.find((s) => s.id === lostSourceId) : null;
  const lostAmount = lost ? Number(lost.amount) || 0 : 0;

  const adjIncome = Math.max(0, (base.monthlyIncome - lostAmount) * (1 - incomeDropPct));
  const baseOutflow = base.committed + base.monthlyVariable + (Number(newDebtMonthly) || 0);
  const adjOutflow = baseOutflow * (1 + expenseUpPct);
  const monthlyBalance = adjIncome - adjOutflow;

  const availableEmergency = base.emergency - (Number(oneTime) || 0);

  // runway = months the emergency fund covers a monthly deficit
  let runwayMonths;
  if (monthlyBalance >= 0) runwayMonths = Infinity;
  else if (availableEmergency <= 0) runwayMonths = 0;
  else runwayMonths = availableEmergency / Math.abs(monthlyBalance);

  const coverageMonths = adjOutflow > 0 ? Math.max(availableEmergency, 0) / adjOutflow : 0;

  // baseline (no shock) for before/after comparison
  const baseBalance = base.monthlyIncome - (base.committed + base.monthlyVariable);
  const baseCoverage = base.committed + base.monthlyVariable > 0 ? base.emergency / (base.committed + base.monthlyVariable) : 0;
  const baseRunway = baseBalance >= 0 ? Infinity : base.emergency / Math.abs(baseBalance);

  return {
    base,
    adjIncome,
    adjOutflow,
    monthlyBalance,
    availableEmergency,
    runwayMonths,
    coverageMonths,
    status: classify(monthlyBalance, runwayMonths, coverageMonths),
    baseline: {
      monthlyBalance: baseBalance,
      runwayMonths: baseRunway,
      status: classify(baseBalance, baseRunway, baseCoverage),
    },
    lostSourceName: lost ? lost.name : null,
  };
}
